import type { ApolloError, DocumentNode, OperationVariables, QueryOptions } from "@apollo/client/core";
import { DefaultContext, MutationOptions } from "@apollo/client/core";
import { isEmpty } from "@/framework/utilities/isEmpty";
import { ZodError } from "zod";

/**
 * Represents a server error exception
 */
export interface GraphQLServerException {
  context: {
    [key: string]: string | number | boolean;
  };
  stacktrace: string[];
}

class GraphqlApiError extends Error {
  /** Error type */
  type: GraphqlApiError.Type;
  /** Error response */
  apolloError?: ApolloError;
  serverException?: GraphQLServerException;
  errorMessage?: string;
  operationName?: string;
  variables?: any;
  parseError?: ZodError;

  constructor(
    type: GraphqlApiError.Type = GraphqlApiError.Type.general,
    apolloError?: ApolloError,
    serverException?: GraphQLServerException,
    errorMessage?: string,
    operationName?: string,
    variables?: any,
    ...params: any[]
  ) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(...params);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, GraphqlApiError);
    }

    this.name = "GraphqlApiError";
    // Custom debugging information
    this.type = type;
    // Backend error response values
    this.apolloError = apolloError;
    this.serverException = serverException;
    this.errorMessage = errorMessage;
    this.operationName = operationName;
    this.variables = variables;
  }

  /**
   * Creates a GraphqlApiError from a caught error
   * @param unknownError error from the catch block
   * @param queryOptions Query Options from Apollo
   * @param mutationOptions Mutation Options from Apollo
   */
  public static create = <TData = any, T = any, TVariables = OperationVariables, TContext = DefaultContext>(
    unknownError: unknown,
    queryOptions: QueryOptions<TVariables, T> | null,
    mutationOptions: MutationOptions<TData, TVariables, TContext> | null
  ): GraphqlApiError => {
    try {
      const res = unknownError as ApolloError;
      let error: GraphqlApiError = new GraphqlApiError(GraphqlApiError.Type.graphqlFailed, res, undefined, res.message); // Default initialization

      console.warn("GraphqlApiError: ", JSON.stringify(res));

      // @ts-ignore
      if (!isEmpty(res.networkError) && res.networkError.statusCode == 500) {
        error = new GraphqlApiError(GraphqlApiError.Type.internalServerError, res, undefined, res.message);
        // @ts-ignore
      } else if (!isEmpty(res.networkError) && res.networkError.statusCode == 400) {
        error = new GraphqlApiError(GraphqlApiError.Type.badRequest, res, undefined, res.message);
        // @ts-ignore
      } else if (!isEmpty(res.networkError) && res.networkError.statusCode == 401) {
        error = new GraphqlApiError(GraphqlApiError.Type.unauthorized, res, undefined, res.message);
      } else if (!isEmpty(res.networkError)) {
        error = new GraphqlApiError(GraphqlApiError.Type.internalServerError, res, undefined, res.message);
      } else if (!isEmpty(res.graphQLErrors)) {
        // Detect if we have a python server error
        error = new GraphqlApiError(GraphqlApiError.Type.graphqlFailed, res, undefined, res.message);
      } else {
        // Unknown error
        error = new GraphqlApiError(GraphqlApiError.Type.unknown, res, undefined, res.message);
      }

      error.operationName = "";
      error.variables = {};

      if (queryOptions != null) {
        if (queryOptions.query.definitions) {
          queryOptions.query.definitions.forEach((definition) => {
            if (definition.kind === "OperationDefinition" && definition.name) {
              error.operationName = definition.name.value;
            }
          });
        }
        error.variables = queryOptions.variables;
      }

      if (mutationOptions != null) {
        if (mutationOptions.mutation.definitions) {
          mutationOptions.mutation.definitions.forEach((definition) => {
            if (definition.kind === "OperationDefinition" && definition.name) {
              error.operationName = definition.name.value;
            }
          });
        }
        error.variables = mutationOptions.variables;
      }

      return error;
    } catch (e) {
      return new GraphqlApiError(
        GraphqlApiError.Type.unknown,
        undefined,
        undefined,
        "Exception occurred in GraphqlApiError.create"
      );
    }
  };

  public static createFromZod = (error: ZodError, apiName?: string): GraphqlApiError => {
    const api = this.getNameFromInput(apiName);
    return new GraphqlApiError(
      GraphqlApiError.Type.parseError,
      undefined,
      undefined,
      error.message,
      api,
      error.message,
      error
    );
  };

  private static getNameFromInput = (input?: DocumentNode | string): string | undefined => {
    let result: string | undefined = undefined;

    if (!input) return result;
    // Check if TypedDocument and get the name
    // @ts-ignore
    if (input.definitions && input.definitions[0] && input.definitions[0].name && input.definitions[0].name.value)
      // @ts-ignore
      result = input.definitions[0].name.value as string;
    // Check if string
    if (typeof input === "string" || input instanceof String) {
      result = input as string;
    }
    return result;
  };
}

// eslint-disable-next-line no-redeclare
namespace GraphqlApiError {
  export enum Type {
    /** A general JavaScript error */
    general = "general",
    /** A general GraphQL error */
    graphqlFailed = "graphqlFailed",
    /** Bad request (400) */
    badRequest = "badRequest",
    /** Non nullable response data was null or undefined */
    noData = "noData",
    /** Unauthorized 401 */
    unauthorized = "unauthorized",
    /** HTTP status not in OK (200<300) range */
    httpFailed = "httpFailed",
    /** Internal server error (500) - Usually a python error */
    internalServerError = "internalServerError",
    /** Getting JWT failed */
    getJwtFailed = "getJwtFailed",
    /** Parsing the response */
    parseError = "parseError",
    /** Unknown error */
    unknown = "unknown",
  }
}

export default GraphqlApiError;
